/**
 * Plan-vs-actual variance for the settling-in Tracking tab (S7).
 *
 * Pure and DST-safe by construction: both sides are reduced to org-zone
 * wall-clock "HH:MM" first and the arithmetic is plain minutes — no `Date`
 * subtraction is involved.
 *
 * The actual side MUST go through `formatOrgTime` (which formats the instant in
 * the org timezone), never a raw browser-local `format(new Date(iso), "HH:mm")` —
 * otherwise a web-entered row shows a phantom ±60/±120 min offset. This is the
 * correction ST-1 (#931) landed and that this helper depends on.
 */

import { formatOrgTime } from '@/utils/dateUtils';

/** "HH:MM" (optionally "HH:MM:SS") → minutes since midnight; null if unparseable. */
function hhmmToMinutes(value: string): number | null {
  const match = value.trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

/**
 * Signed minutes: actual − planned. `null` when either side is absent or
 * unparseable.
 *
 * @param plannedHHMM The schedule entry's TIME field. `convertPgTimeToString`
 *   formats it as exactly "HH:MM", but this parses defensively anyway.
 * @param actualIso The tracking entry's TIMESTAMPTZ instant.
 */
export function varianceMinutes(
  plannedHHMM: string | undefined | null,
  actualIso: string | undefined | null,
): number | null {
  if (!plannedHHMM || !actualIso) return null;

  const planned = hhmmToMinutes(plannedHHMM);
  if (planned === null) return null;

  // Org-zone wall-clock, NOT the browser's — see the module note.
  const actualWallClock = formatOrgTime(actualIso);
  if (!actualWallClock) return null;

  const actual = hhmmToMinutes(actualWallClock);
  if (actual === null) return null;

  return actual - planned;
}
